<template>
	<div
		:class="[
			'menu-button-v2',
			variantName ? 'menu-button-v2--' + variantName : '',
			disabled ? 'is-disabled' : ''
		]"
		:id="uid"
		ref="dropdown"
	>
		<Spinner
			class="btn-spinner-overlay"
			v-if="isLoading"
			:processor="{ loading: true }"
			:variant="'buttons'"
			data-test="spinner"
		/>

		<div
			class="menu-button-v2__container"
			@click="displayMenu = !displayMenu"
			:style="widthStyle"
			ref="dropdownPosRef"
			tabindex="0"
		>
			<i
				v-if="iconClassName"
				:class="['icon menu-button-v2__icon', iconClassName]"
			></i>

			<div class="menu-button-v2__text">
				{{ placeholder }}
			</div>

			<i
				:class="[
					'icon menu-button-v2__trigger',
					displayMenu ? 'icon-caret_up' : 'icon-caret_down'
				]"
			></i>
		</div>

		<DropdownListV2
			v-show="displayMenu"
			ref="dropdownListRef"
			:options="options"
			:value="value"
			@change="emitSelection"
		/>
		<PopperPosition
			trackedRefName="dropdownPosRef"
			popoverRefName="dropdownListRef"
			placement="bottom-start"
			:popperVisible="displayMenu"
			:altBoundary="altBoundary"
			@popperMinWidthChanged="setpopperMinWidth"
			@positionUpdated="updateIconOrientation"
		/>
	</div>
</template>

<script>
import DropdownListV2 from '@/components/forms/dropdown/dropdown-list-v2';
import PopperPosition from '@/components/visual/popper-position';
import Spinner from '@/components/visual/spinner/spinner';

// mixins
import DocumentClick from '@/mixins/document-click';
import popperPositionMixin from '@/mixins/popper-position-mixin';

export default {
	name: 'MenuButtonV2',
	components: {
		DropdownListV2,
		Spinner,
		PopperPosition
	},
	mixins: [DocumentClick('closeMenu'), popperPositionMixin],
	model: {
		prop: 'value',
		event: 'selected'
	},
	props: {
		value: [String, Number, Boolean],
		defaultValue: [String, Number, Boolean],
		options: {
			type: Array,
			required: true
		},
		altBoundary: {
			type: String,
			default: undefined
		},
		placeholder: String,
		variantName: String,
		iconClassName: String,
		width: String,
		isLoading: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			displayMenu: false,
			popperMinWidth: 'auto'
		};
	},
	computed: {
		widthStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			}
			return {
				width: this.width
			};
		}
	},
	methods: {
		closeMenu() {
			this.displayMenu = false;
		},
		emitSelection(value) {
			this.displayMenu = false;
			this.$emit('selected', value);
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Menu Button V2 Styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.menu-button-v2 {
	position: relative;
	display: inline-flex;

	&__container {
		-webkit-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
		user-select: none;
		cursor: pointer;
		outline: 0;
		border-radius: $btn-border-radius;
		border: $btn-border-width solid $base;
		background-color: $btn-default-bkgd;
		padding: $btn-vertical-padding $btn-horizontal-padding;
		@include btn-height;
		@include text-contrast($btn-default-bkgd, $white, $black);
		font-weight: $btn-font-weight;
		font-size: $btn-font-size;
		display: flex;
		align-items: center;

		&:focus,
		&:hover,
		&:active {
			background-color: $base;
			@include text-contrast($base, $white, $black);
		}
	}

	&__icon {
		padding-right: $input-padding-x;
	}

	&__text {
		width: 100%;

		text-overflow: ellipsis;
		white-space: nowrap;
		overflow: hidden;
	}

	&__trigger {
		padding-left: $input-padding-x;
		font-size: 8px;
		z-index: 0;
	}
}

/*------------------------------------*\
#PRIMARY
\*------------------------------------*/
.menu-button-v2--primary {
	.menu-button-v2__container {
		background-color: $btn-primary-bkgd;
		@include text-contrast($btn-primary-bkgd, $white, $black);

		&:focus,
		&:hover,
		&:active {
			background-color: $btn-primary-bkgd-hover;
			@include text-contrast($btn-primary-bkgd-hover, $white, $black);
		}
	}
}

/*------------------------------------*\
#SECONDARY
\*------------------------------------*/
.menu-button-v2--secondary {
	.menu-button-v2__container {
		background-color: $btn-secondary-bkgd;
		@include text-contrast($btn-secondary-bkgd, $white, $black);
		border-color: $btn-secondary-bkgd;

		&:focus,
		&:hover,
		&:active {
			background-color: $btn-secondary-bkgd-hover;
			@include text-contrast($btn-secondary-bkgd-hover, $white, $black);
			border-color: $btn-secondary-bkgd-hover;
		}
	}
}

/*------------------------------------*
#MENU BUTTON SPINNER
\*------------------------------------*/
.menu-button-v2 {
	.btn-spinner-overlay {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		margin: auto;
		background-color: rgba($black, 0.6);
		border-radius: $btn-border-radius;
		backdrop-filter: blur(2px);
		z-index: 5;
		cursor: wait;
	}
}
</style>
